import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import BackToTop from '@/components/feature/BackToTop';
import SEOHead from '@/components/feature/SEOHead';
import { categories, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'competitive-shooting');

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug) || categories[0],
    [activeSlug],
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === activeSlug),
    [activeSlug],
  );

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: '赛事分类', path: '/category/competitive-shooting' },
    { name: activeCategory.name },
  ];

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${activeCategory.name} - 17c.cc每日大赛`,
      description: activeCategory.description,
      url: `https://17c.cc/category/${activeCategory.slug}`,
      isPartOf: { '@type': 'WebSite', name: '17c.cc每日大赛', url: 'https://17c.cc' },
    },
  ];

  return (
    <>
      <SEOHead
        title={`${activeCategory.name} - 17c.cc每日大赛 | 在线竞技赛事分类`}
        description={`${activeCategory.description}。17c.cc每日大赛${activeCategory.name}频道，提供最新攻略、赛事资讯和技巧教程。`}
        keywords={`${activeCategory.name},17c.cc每日大赛,在线竞技,赛事分类,每日大赛`}
        canonicalUrl={`https://17c.cc/category/${activeCategory.slug}`}
        schema={schema}
      />
      <Navbar />
      <BackToTop />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto pt-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* Category Hero */}
          <div className="relative h-64 md:h-80 overflow-hidden">
            <img
              src={activeCategory.image}
              alt={activeCategory.name}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-500 flex items-center justify-center">
                    <i className={`${activeCategory.icon} text-background-50 text-lg`}></i>
                  </div>
                  <h1 className="font-heading text-2xl md:text-4xl font-bold text-white">
                    {activeCategory.name}
                  </h1>
                </div>
                <p className="text-white/80 text-sm md:text-base max-w-xl">
                  {activeCategory.description}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="bg-background-100 border-b border-background-200/70 sticky top-16 z-30">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-none">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                  <span className={`text-xs ${activeSlug === cat.slug ? 'text-background-50/70' : 'text-foreground-400'}`}>
                    {cat.articleCount}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Article List */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group rounded-lg bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                        <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">{article.category}</span>
                        <span>{article.date}</span>
                        <span>{article.readTime}</span>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="text-xs px-2 py-0.5 rounded bg-secondary-100 text-secondary-700">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-inbox-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}